import './drawermenu-base.css';

import { PropsWithChildren, Ref, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { NavItemTypes } from '../../../../services/SystemNames';
import { isArrayEmpty, isNullOrEmpty } from '../../../helpers/ObjectHelpers';
import { useLocation, useNavigate } from 'react-router';
import { isLoggedIn } from '../../../helpers/AuthHelpers';
import { DialogProperties } from '../../dialogs/DialogProperties';
import { Dialog } from '../../dialogs/Dialog';
import { getTokenExpireDate } from '../../../helpers/TokenHelpers';
import packageJson from '../../../../../package.json';
import Overlay from '../../overlay/Overlay';
import MetaIdbRepository from '../../../../repositories/idb/MetaIdbRepository';
import { IdbRepository as EvaluationIdbRepository } from '@pilot-web/pec-exam-evaluation';
import PilotAssignmentIdbRepository from '../../../../repositories/idb/PilotAssignmentIdbRepository';
import PilotApiRepository from '../../../../repositories/api/PilotApiRepository';
import { convertPilotAssignmentApiDtoToIdb } from '../../../../pages/pilot/pilot-assignment/helpers/PilotAssignmentHelpers';
import { initCacheAsync } from '../../../helpers/CacheHelpers';
import { Drawer } from './components/Drawer';
import { DrawerMenuRepository, DrawerMenuRepositoryInterface } from './repositories/DrawerMenuRepository';
import { getElementsByClassName } from '../../../helpers/ElementHelpers';
import { DrawerMenuService, IDrawerMenuService } from './DrawerMenuService';
import { handleLoginAsync } from "../../../helpers/AuthHelpers";
import { ImpersonateDialog } from "@pilot-web/settings-impersonate";
import { useApplicationRights, useConfiguration, useSetRefreshedData, useIsMapMini, useSuccessNotification } from "components/hooks/useAppStore";

interface DrawerMenuRef {
    toggle: () => void,
}
 
interface DrawerMenuProps {
    ref: Ref<DrawerMenuRef>,
    newVersionRef: any,
    isNewVersionAvailable: boolean,
    setIsNewVersionAvailable: (isNewVersionAvailable: boolean) => void,
}

export const DrawerMenu = ({
    ref,
    children,
    newVersionRef,
    isNewVersionAvailable,
    setIsNewVersionAvailable,
}: PropsWithChildren<DrawerMenuProps>) => {

    useImperativeHandle(ref, () => ({
        async toggle() {
            setExpanded(!expanded);
            await repository.setMapExpandedAsync(!expanded);
        }
    }));

    const defaultTitle = "Njord Pilot Web";

    const applicationRights = useApplicationRights();
    const configuration = useConfiguration();
    const onRefreshedData = useSetRefreshedData();
    const isMenuMini = useIsMapMini();
    const addSuccessNotification = useSuccessNotification();
    
    const repository: DrawerMenuRepositoryInterface = new DrawerMenuRepository();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [expanded, setExpanded] = useState<boolean>(false);
    const [items, setItems] = useState<any[]>([]);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
    const [environment, setEnvironment] = useState<string>("");
    const [showRefreshDialog, setShowRefreshDialog] = useState<boolean>(false);
    const [showImpersonateDialog, setShowImpersonateDialog] = useState<boolean>(false);
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const [mode, setMode] = useState<string>("overlay");
    const [mini, setMini] = useState<boolean>(false);
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
    
    // Memoize menu items to prevent recreation on every render
    const menuItems = useMemo(() => {
        if (!isLoggedIn() || !applicationRights) return [];
        const dsService: IDrawerMenuService = new DrawerMenuService(applicationRights);
        return dsService.getMenuItems();
    }, [applicationRights, isOnline]);
    
    useEffect(() => {
        setItems(menuItems);
    }, [menuItems]);

    useEffect(() => {
        if (!configuration) return;
        setEnvironment(configuration.environment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuration]);

    useEffect(() => {
        initializePreferencesAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMenuMini]);

    useEffect(() => {
        initializePreferencesAsync();
        onResize();

        window.addEventListener("resize", onResize);
        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);

        return (() => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        })
    }, []);

    useEffect(() => {
        if (mode === "push") return;
        setExpanded(false);
    }, [location]);

    useEffect(() => {
        toggleNewVersionMenuItem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewVersionAvailable]);

    return (
        <>
            <Drawer
                isNewVersionAvailable={isNewVersionAvailable}
                expanded={expanded}
                setExpanded={async (e: boolean) => {
                    setExpanded(e);
                    await repository.setMapExpandedAsync(e);
                }}
                onItemClick={onItemClick}
                mode={mode}
                items={items}
                mini={mode === "overlay" ? false : mini}>
                {children}
            </Drawer>

            {
                (showDialog && !isLoggingOut) &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        title: defaultTitle,
                        onClose: () => setShowDialog(false),
                        actions: [
                            {
                                onClick: () => setShowDialog(false),
                                icon: "ok",
                                themeColor: "primary",
                                text: "Ok"
                            }
                        ]
                    }}>
                    <div>Versjon: {packageJson.version}</div>
                    {
                        !isNullOrEmpty(environment) && <div>Milj&oslash;: {environment}</div>
                    }
                    <div>Exp: {getTokenExpireDate()}</div>
                </Dialog>
            }

            {
                (showDialog && isLoggingOut) &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        mode: "confirmation",
                        title: "Logg ut",
                        text: "&Oslash;nsker du &aring; logge ut av NjordPilotWeb?",
                        onClose: () => setShowDialog(false),
                        onClick: onConfirmLogut
                    }} />
            }

            {
                showRefreshDialog &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        mode: "confirmation",
                        title: "Resette data",
                        text: "&Oslash;nsker du &aring; resette data? B&aring;de losoppdrag og evalueringer etter farledspr&oslash;ve vil bli tilbakestilt til data lagret p&aring; server.",
                        onClose: () => setShowRefreshDialog(false),
                        onClick: refreshAsync
                    }} />
            }

            {
                showImpersonateDialog &&
                <ImpersonateDialog onClose={() => setShowImpersonateDialog(false) } />
            }
            <Overlay isBusy={isBusy} />
        </>

        // might be implemented later
        //{
        //    showSearchDialog &&
        //    <NavigationMobileSearchDialog
        //        setShowSearchDialog={setShowSearchDialog}
        //    />
        //}
    )

    function onResize() {
        const isPush = window.innerWidth >= 800;
        setMode(isPush ? "push" : "overlay");
    }

    async function initializePreferencesAsync() {
        if (!isLoggedIn()) {
            setMini(false);
            return;
        }
        const preferencesMiniMap = await MetaIdbRepository.getPreferencesMapMiniAsync();
        setMini(preferencesMiniMap.value);

        const isPush = window.innerWidth >= 800;
        if (!isPush) return;

        const expanded = await repository.getMapExpandedAsync();
        setExpanded(expanded);
    }

    function handleOffline() {
        setIsOnline(false);
    }

    function handleOnline() {
        setIsOnline(true);
    }

    function onItemClick(e: any) {
        switch (e.type) {
            case NavItemTypes.Login:
                onLogin();
                break;

            case NavItemTypes.Logout:
                onLogout();
                break;

            case NavItemTypes.About:
                onAbout();
                break;

            case NavItemTypes.RefreshData:
                setShowRefreshDialog(true);
                break;

            case NavItemTypes.Impersonate:
                setShowImpersonateDialog(true);
                break;

            case NavItemTypes.NewVersion:
                newVersionRef.current.reload();
                setIsNewVersionAvailable(false);
                break;
            default:
                break;
        }
        if (mode === "push") return;
        setExpanded(false);
    }

    function onLogout() {
        setIsLoggingOut(true);
        setShowDialog(true);
        setExpanded(false);
    }

    function onConfirmLogut() {
        setShowDialog(false);
        setMini(false);
        navigate("/logout");
    }

    function onAbout() {
        setIsLoggingOut(false);
        setShowDialog(true);
    }

    function onLogin() {
        handleLoginAsync(window.location.href);
    }

    async function refreshAsync() {
        setShowRefreshDialog(false);
        setIsBusy(true);
        
        await MetaIdbRepository.deleteCacheLastExpireDateAsync();
        await initCacheAsync(configuration);
        await EvaluationIdbRepository.deleteAllAsync();
        await PilotAssignmentIdbRepository.deleteAllAsync();
        await PilotApiRepository.deleteAllPushSubscriptionsAsync();

        const response:any = await PilotApiRepository.getAssignedPilotagesAsync();
        if (response.ok === true) {
            const data = await response.json();
            data.forEach(async (dto:any) => {
                const pilotAssignment = convertPilotAssignmentApiDtoToIdb(dto);
                await PilotAssignmentIdbRepository.setAsync(pilotAssignment);
            });
        }
        
        setIsBusy(false);

        addSuccessNotification("Data er resatt");
        onRefreshedData();
    }

    function toggleNewVersionMenuItem() {

        const elems = getElementsByClassName("drawer-item-new-version");
        if (isArrayEmpty(elems)) return;

        const elem:any = elems[0];
        if (!elem) return;

        if (isNewVersionAvailable) {
            elem.style.display = "grid";
        } else {
            elem.style.display = "none";
        }
    }
}


